import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";
import { agentTenderService } from "../../services/agentTenderApi";
import {
  getCachedVerifyLinks,
  setCachedVerifyLinks,
} from "./verifyLinkCache";
import { CloseGlyph, QrGlyph } from "./glyphs";
import {
  formatPositionValue,
  formatTypeOfTender,
  formatStaffRole,
} from "../../constants/agentTenderChoices";

// Verify surface V2 (design doc 03, screen 6) — presentation rebuilt 1:1 from
// the handoff artboards (UI REBUILD 2026-07-22). Desktop ≥768 is the wide
// three-way modal; ≤768 the full-screen bottom sheet with the combined
// QR + link card. All icons are the artboard SVGs, copied verbatim (Rule 2).
//
// Three EQUAL ways to reach the club contact — QR, link, email — armed
// TOGETHER by the honesty gate: one tick mints the signed token and unlocks
// all three. The club contact email is OPTIONAL (design doc 03 screen 6:
// "QR + link ready on open · email is optional · three equal ways"; the
// DESIGN_PROMPT's G-1 fix: "QR + link are ALWAYS available … no longer chained
// to a successful email send"). The tick — never an address — is what arms.
// The mint and the mail are decoupled backend-side: arming without an address
// yields a working QR + link and sends nothing; typing one afterwards sends on
// the SAME token, so a QR already handed out never dies.
// Send errors are VISIBLE (email_sent is honest — a failed mail shows a real
// error and points at the QR/link, which work on their own; email_attempted
// keeps a no-mail arm from reading as a failure), the 10/day throttle only
// ever limits the email path, and re-display is unlimited via the client-side
// link cache ("Show QR again" on the journey row).
// Only the CLUB enters a phone number, on the public page — never the agent
// here (Brief Rule 10).

// ── Artboard SVGs (doc 03, screen 6 — verbatim) ──────────────────────────────
// Close X + QR frame come from the shared glyph set (glyphs.jsx) — same
// verbatim artboard SVGs, defined once.
const IconCheck = ({ size = 14, stroke = "currentColor", strokeWidth = "2.4" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12l5 5L20 7" />
  </svg>
);
const IconDownload = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12M7 10l5 5 5-5" />
    <path d="M4 21h16" />
  </svg>
);
const IconCopy = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);
const IconShare = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);
const IconSend = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
);
const IconAlert = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16.5h.01" />
  </svg>
);
const IconClock = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const IconInfo = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1B6FE0" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M12 12v4" />
  </svg>
);
const IconFile = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

// ≤768 the surface is a full-screen bottom sheet (design: "the modal becomes
// a full-screen sheet ≤768"). Presentation only — same state, same handlers.
const useIsDesktop = () => {
  const query = "(min-width: 768px)";
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isDesktop;
};

const VerifyNowModal = ({ isOpen, onClose, tender, tenderId, defaultEmail = "", onArmed }) => {
  const id = tender?.id ?? tenderId;
  const clubName = tender?.club?.name || "";
  const [email, setEmail] = useState(defaultEmail);
  const [emailError, setEmailError] = useState("");
  // T005: a refused tick used to revert the checkbox with NO feedback at the
  // click point — the only message sat in the email card, possibly off-viewport.
  // This carries the same message next to the gate itself.
  const [gateError, setGateError] = useState("");
  const [gateChecked, setGateChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  // The QR encodes qrUrl and the copy/share buttons use linkUrl — both carry
  // a ?c= channel tag so the backend logs how the club arrived (link vs QR).
  const [qrUrl, setQrUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [onePagerUrl, setOnePagerUrl] = useState("");
  // banner: null | {kind: "sent", email} | {kind: "send_error"} | {kind: "throttle"}
  const [banner, setBanner] = useState(null);
  const [copied, setCopied] = useState(false);
  const qrWrapRef = useRef(null);
  // T005: only one of desktop/sheet is mounted at a time, so a single ref
  // always points at the live email input — used to bring the refused field
  // into view (same scrollIntoView pattern as the send-error banner's Show QR).
  const emailFieldRef = useRef(null);
  const isDesktop = useIsDesktop();

  // (Re-)initialise per open: a previously armed tender re-opens straight in
  // the armed state from the link cache — "Show QR again" never resends.
  useEffect(() => {
    if (!isOpen) return;
    const cached = getCachedVerifyLinks(id);
    if (cached) {
      setQrUrl(cached.qrUrl);
      setLinkUrl(cached.linkUrl);
      setOnePagerUrl(cached.onePagerUrl || "");
      setEmail(cached.email || defaultEmail);
      setArmed(true);
      setGateChecked(true);
    } else {
      setQrUrl("");
      setLinkUrl("");
      setOnePagerUrl("");
      setEmail(defaultEmail);
      setArmed(false);
      setGateChecked(false);
    }
    setBanner(null);
    setEmailError("");
    setGateError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, id]);

  if (!isOpen) return null;

  const title =
    tender?.tender_category === "staff"
      ? formatStaffRole(tender?.staff_role, tender?.other_staff_position)
      : formatPositionValue(tender?.position, tender?.other_staff_position);
  const contextPill = [title, formatTypeOfTender(tender?.type_of_tender), clubName]
    .filter(Boolean)
    .join(" · ");

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  // The signed token lives inside the armed URLs. Handing it back on a later
  // call lets the backend reuse the SAME verification instead of minting a
  // competing second one — the QR the agent already shared stays valid.
  const armedToken = () =>
    (/\/verify\/agent-tender\/([^/?#]+)/.exec(linkUrl) || [])[1] || "";

  // Bring the email field to the user and focus it (jsdom has no
  // scrollIntoView — guard so the refusal path stays safe there).
  const revealEmailField = () => {
    const field = emailFieldRef.current;
    if (typeof field?.scrollIntoView === "function") {
      field.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    field?.focus?.({ preventScroll: true });
  };

  // One POST arms everything: it mints the token (QR + link) and, ONLY when an
  // address is present, also sends the email. Used by the honesty gate (first
  // arm) and by the Send/Resend button alike.
  const arm = async ({ fromGate = false, fromSend = false } = {}) => {
    if (busy) return;
    const trimmed = email.trim();
    // The address is OPTIONAL — an empty field arms QR + link and sends
    // nothing. Only a MALFORMED address is refused (T005: say it at the click
    // point, not just in the email card, which can sit off-viewport).
    if (trimmed && !validEmail(trimmed)) {
      const msg = "That isn't a valid email address. Correct it, or clear the field to continue without one.";
      setEmailError(msg);
      if (fromGate) {
        setGateError(msg);
        setGateChecked(false);
      }
      revealEmailField();
      return;
    }
    // The email way itself needs an address; the tick alone already gave the
    // agent a working QR and link, so there is nothing to fall back on here.
    if (fromSend && !trimmed) {
      setEmailError("Enter the club contact email to send it.");
      revealEmailField();
      return;
    }
    setEmailError("");
    setGateError("");
    setBusy(true);
    const res = await agentTenderService.requestVerification(
      id,
      trimmed,
      armedToken(),
    );
    setBusy(false);
    if (res.success) {
      const nextQr = res.data?.qr_url || res.data?.verify_url || "";
      const nextLink = res.data?.link_url || res.data?.verify_url || "";
      setQrUrl(nextQr);
      setLinkUrl(nextLink);
      setOnePagerUrl(res.data?.one_pager_url || "");
      setArmed(true);
      // email_attempted separates "no mail was asked for" from "the mail
      // failed"; email_sent is HONEST, so a false there is a REAL failure —
      // show the error and point at the QR/link (they work on their own).
      const attempted = res.data?.email_attempted ?? !!trimmed;
      setBanner(
        !attempted
          ? null
          : res.data?.email_sent === false
            ? { kind: "send_error" }
            : { kind: "sent", email: trimmed },
      );
      setCachedVerifyLinks(id, {
        qrUrl: nextQr,
        linkUrl: nextLink,
        onePagerUrl: res.data?.one_pager_url || "",
        email: trimmed,
        verificationId: res.data?.verification_id,
      });
      if (onArmed) onArmed();
    } else if (res.status === 429) {
      // Only the email path is limited — armed QR/link stay fully usable.
      setBanner({ kind: "throttle" });
      if (fromGate && !armed) setGateChecked(false);
    } else {
      toast.error(res.error || "We couldn't start the verification. Please try again.");
      if (fromGate && !armed) setGateChecked(false);
    }
  };

  const handleGateToggle = () => {
    if (busy) return;
    if (!gateChecked) {
      setGateError("");
      setGateChecked(true);
      if (!armed) arm({ fromGate: true });
    } else if (!armed) {
      setGateChecked(false);
    }
    // Once armed the tick cannot be taken back — the links exist.
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy the link.");
    }
  };

  const handleShareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Confirm a request on TransferPitch",
          text: "Please confirm this request on TransferPitch.",
          url: linkUrl,
        });
        return;
      } catch (e) {
        // User dismissed the share sheet, or share failed — fall through to copy.
        if (e?.name === "AbortError") return;
      }
    }
    handleCopy();
  };

  const getQrPngBlob = () =>
    new Promise((resolve) => {
      const canvas = qrWrapRef.current?.querySelector("canvas");
      if (!canvas) return resolve(null);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });

  const handleQrShareOrDownload = async () => {
    const blob = await getQrPngBlob();
    if (!blob) {
      toast.error("Couldn't prepare the QR code.");
      return;
    }
    const file = new File([blob], "transferpitch-verification-qr.png", { type: "image/png" });
    // Prefer the native share sheet with the image file where supported.
    if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
      try {
        await navigator.share({
          files: [file],
          title: "Confirm a request on TransferPitch",
        });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
        // Fall through to download on failure.
      }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transferpitch-verification-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("QR code downloaded.");
  };

  const disabledWay = !armed;
  // Way buttons per artboard: 38px tall (40 in the sheet), radius 9; the
  // inactive state is the greyed #F1F5F9 / #94A3B8 look from the gate card.
  const wayBtn = (extra, mobile = false) =>
    `flex items-center justify-center gap-1.5 ${mobile ? "h-10" : "h-[38px]"} px-3 rounded-[9px] text-[12.5px] font-semibold ${
      disabledWay
        ? "border border-[#E2E8F0] bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed"
        : extra
    }`;

  // Honesty gate — one tick arms QR, link and email together. The artboard
  // checkbox: 17px, 1.5px #CBD5E1 border unchecked, solid #2283FF + white
  // check when ticked. The real input stays for a11y/tests (sr-only).
  const gateBox = (compact = false) => (
    <div className={`rounded-[11px] border border-[#FDE68A] bg-[#FFFBEB] ${compact ? "px-3 py-2.5" : "px-3.5 py-[11px]"}`}>
      <label className={`flex items-start gap-2.5 ${busy || armed ? "" : "cursor-pointer"}`}>
        <input
          type="checkbox"
          checked={gateChecked}
          disabled={busy || armed}
          onChange={handleGateToggle}
          className="sr-only"
        />
        {gateChecked ? (
          <span className="flex-none w-[17px] h-[17px] rounded-[5px] bg-[#2283FF] inline-flex items-center justify-center mt-px" aria-hidden="true">
            <IconCheck size={11} stroke="#fff" strokeWidth="3.4" />
          </span>
        ) : (
          <span className="flex-none w-[17px] h-[17px] rounded-[5px] border-[1.5px] border-[#CBD5E1] bg-white mt-px" aria-hidden="true" />
        )}
        {armed && compact ? (
          <span className="text-[11.5px] text-[#78350F]">
            <strong>Right contact confirmed.</strong> A knowingly false contact
            counts as fraud and can lead to permanent removal.
          </span>
        ) : (
          <span className="text-xs text-[#78350F] leading-relaxed">
            <strong>
              This is the right contact{clubName ? ` for ${clubName}` : ""}.
            </strong>{" "}
            I know that knowingly naming a false contact counts as fraud and
            can lead to permanent removal from TransferPitch.
            {armed && (
              <span className="text-[#B45309]">
                {" "}· required before any of the three ways activates
              </span>
            )}
          </span>
        )}
      </label>
      {/* T005: the tick was refused — say so right here, at the click point.
          Reuses the modal's red error styling (IconAlert + #B91C1C, as in the
          send-error banner). */}
      {gateError && (
        <p
          role="alert"
          className="text-[11.5px] font-semibold text-[#B91C1C] mt-1.5 pl-[27px] flex items-center gap-1.5"
        >
          <span className="flex-none"><IconAlert size={13} /></span>
          {gateError}
        </p>
      )}
      {!armed && !gateError && (
        <p className="text-[11px] text-[#94A3B8] mt-1.5 pl-[27px]">
          Confirm the contact first · all three ways stay inactive until the
          box is ticked.
        </p>
      )}
    </div>
  );

  const emailField = (mobile = false) => (
    <>
      <input
        ref={emailFieldRef}
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (emailError) setEmailError("");
          if (gateError) setGateError("");
        }}
        placeholder="Club contact email…"
        className={`w-full ${mobile ? "h-10" : "h-[38px]"} px-[11px] border rounded-[9px] text-[12.5px] text-[#334155] placeholder-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          emailError ? "border-red-500" : "border-[#E2E8F0]"
        }`}
      />
      {emailError && <p className="text-xs text-red-500">{emailError}</p>}
    </>
  );

  const sendEmailBtn = (mobile = false) => (
    <button
      onClick={() => arm({ fromSend: true })}
      disabled={disabledWay || busy}
      className={wayBtn("border border-[#BFDBFE] bg-[#E8F1FF] text-[#1B6FE0] hover:bg-blue-100", mobile)}
    >
      <IconSend />
      {busy ? "Sending…" : armed ? "Resend verification email" : "Send verification email"}
    </button>
  );

  // Honest outcome banners (design: send error / throttle / success).
  const banners = (
    <>
      {banner?.kind === "send_error" && (
        <div className="mt-3.5 rounded-xl border border-[#FECACA] bg-white px-4 py-3.5 flex gap-2.5">
          <span className="flex-none mt-px"><IconAlert /></span>
          <div className="flex flex-col gap-2 flex-1">
            <p className="text-[13px] font-bold text-[#B91C1C]">
              The verification email couldn't be sent.
            </p>
            <p className="text-xs text-[#475569]">
              Your QR and link still work on their own. Share one of them
              instead, or try the email again later.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => qrWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="border border-[#E2E8F0] bg-white text-[#334155] text-xs font-semibold h-[34px] px-3 rounded-lg inline-flex items-center gap-1.5"
              >
                <QrGlyph /> Show QR
              </button>
              <button
                onClick={handleCopy}
                className="border border-[#E2E8F0] bg-white text-[#334155] text-xs font-semibold h-[34px] px-3 rounded-lg inline-flex items-center gap-1.5"
              >
                <IconCopy size={12} /> Copy link
              </button>
            </div>
          </div>
        </div>
      )}
      {banner?.kind === "throttle" && (
        <div className="mt-3.5 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3.5 flex gap-2.5">
          <span className="flex-none mt-px"><IconClock /></span>
          <p className="text-[12.5px] text-[#78350F]">
            <strong>Resend available tomorrow.</strong> Sending is limited to
            10 per day. Showing the QR or copying the link is never limited.
          </p>
        </div>
      )}
      {banner?.kind === "sent" && (
        <div className="mt-3.5 rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] px-4 py-3.5 flex gap-2.5">
          <span className="flex-none mt-px"><IconCheck size={15} stroke="#047857" /></span>
          <p className="text-[12.5px] text-[#065F46]">
            <strong>Verification email sent to {banner.email}.</strong>{" "}
            We'll notify you the moment the club responds.
          </p>
        </div>
      )}
    </>
  );

  const onePagerLink = armed && onePagerUrl && (
    <a
      href={onePagerUrl}
      className="flex items-center justify-center gap-1.5 text-xs font-medium text-[#1B6FE0] hover:underline"
      download
    >
      <IconFile size={13} /> Download one-pager (PDF)
    </a>
  );

  // ── Desktop ≥768: wide modal, three equal way-cards (artboard 1440) ────────
  const desktop = (
    <div className="fixed inset-0 bg-[rgba(15,23,42,0.42)] flex items-start justify-center z-50 p-4 pt-10 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-[1000px] shadow-[0_24px_64px_rgba(15,23,42,0.28)] overflow-hidden">
        <div className="flex items-center gap-2.5 px-6 py-4 border-b border-[#EEF2F7]">
          <h3 className="text-[15px] font-bold text-[#0F172A]">Verify your request</h3>
          {contextPill && (
            <span className="text-[11px] font-semibold px-[9px] py-[3px] rounded-full bg-[#F1F5F9] text-[#475569]">
              {contextPill}
            </span>
          )}
          <span className="flex-1" />
          <button
            onClick={onClose}
            className="w-[30px] h-[30px] rounded-lg border border-[#E2E8F0] inline-flex items-center justify-center text-[#64748B] hover:text-[#0F172A]"
            aria-label="Close"
          >
            <CloseGlyph />
          </button>
        </div>

        <div className="px-6 pt-5 pb-2 flex items-center gap-2">
          {armed ? (
            <>
              <span className="flex-none"><IconCheck size={14} stroke="#047857" /></span>
              <span className="text-[12.5px] text-[#334155]">
                Your QR code and link are ready. Choose any way to reach your
                club contact; email is optional.
              </span>
            </>
          ) : (
            <span className="text-[12.5px] text-[#334155]">
              Ask your club contact to confirm this request. Once they do, your
              request shows a Club Verified badge.
            </span>
          )}
        </div>

        <div className="mx-6 mt-2.5">{gateBox()}</div>

        {/* Three equal ways. */}
        <div className="grid grid-cols-3 gap-4 px-6 pt-3.5 pb-5">
          {/* 1 · Show + save QR */}
          <div className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col gap-2.5 items-center">
            <div className="self-stretch flex items-center gap-[7px]">
              <span className="w-[22px] h-[22px] rounded-full bg-[#E8F1FF] text-[#1B6FE0] text-[11px] font-extrabold inline-flex items-center justify-center">
                1
              </span>
              <span className="text-[12.5px] font-bold">Show + save QR</span>
            </div>
            <div className="flex justify-center" ref={qrWrapRef}>
              {armed && qrUrl ? (
                <div className="w-[170px] h-[170px] p-2 bg-white border border-[#EEF2F7] rounded-[10px]">
                  <QRCodeCanvas value={qrUrl} size={152} marginSize={3} />
                </div>
              ) : (
                <div
                  className="w-[170px] h-[170px] rounded-[10px] bg-gray-100 border border-dashed border-[#E2E8F0]"
                  data-testid="qr-placeholder"
                />
              )}
            </div>
            <button
              onClick={handleQrShareOrDownload}
              disabled={disabledWay}
              className={`self-stretch ${wayBtn("border border-[#E2E8F0] bg-white text-[#334155] hover:bg-gray-50")}`}
            >
              <IconDownload /> Save as PNG
            </button>
            {onePagerLink}
            <p className="text-[11px] text-[#94A3B8] text-center">
              Show it in a meeting or send the PNG. Re-display is unlimited.
            </p>
          </div>

          {/* 2 · Copy or share link */}
          <div className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col gap-2.5">
            <div className="flex items-center gap-[7px]">
              <span className="w-[22px] h-[22px] rounded-full bg-[#E8F1FF] text-[#1B6FE0] text-[11px] font-extrabold inline-flex items-center justify-center">
                2
              </span>
              <span className="text-[12.5px] font-bold">Copy or share link</span>
            </div>
            <input
              readOnly
              value={armed ? linkUrl : ""}
              placeholder="Link appears once armed"
              className="w-full h-[38px] px-[11px] border border-[#E2E8F0] rounded-[9px] text-xs text-[#475569] bg-[#F8FAFC] font-mono overflow-hidden whitespace-nowrap"
            />
            <button
              onClick={handleCopy}
              disabled={disabledWay}
              className={wayBtn("bg-[#2283FF] text-white hover:bg-[#1B6FE0]")}
            >
              {copied ? <IconCheck size={13} /> : <IconCopy />} Copy link
            </button>
            <button
              onClick={handleShareLink}
              disabled={disabledWay}
              className={wayBtn("border border-[#E2E8F0] bg-white text-[#334155] hover:bg-gray-50")}
            >
              <IconShare /> Share…
            </button>
            <p className="text-[11px] text-[#94A3B8]">
              WhatsApp, iMessage, wherever your contact lives. Same signed
              link as the QR.
            </p>
          </div>

          {/* 3 · Send by email (optional) */}
          <div className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col gap-2.5">
            <div className="flex items-center gap-[7px]">
              <span className="w-[22px] h-[22px] rounded-full bg-[#E8F1FF] text-[#1B6FE0] text-[11px] font-extrabold inline-flex items-center justify-center">
                3
              </span>
              <span className="text-[12.5px] font-bold">
                Send by email{" "}
                <span className="font-medium text-[#94A3B8]">(optional)</span>
              </span>
            </div>
            {emailField()}
            {sendEmailBtn()}
            <p className="text-[11px] text-[#94A3B8]">
              Sending is limited to 10 per day. The QR and link are never
              limited.
            </p>
          </div>
        </div>

        <div className="px-6 -mt-1 pb-1">{banners}</div>

        {/* How the club uses it. */}
        <div className="border-t border-[#EEF2F7] px-6 pt-3.5 pb-[18px] flex flex-col gap-1.5 mt-3.5">
          <div className="flex items-center gap-2">
            <span className="flex-none"><IconInfo /></span>
            <span className="text-xs text-[#475569]">
              <strong>How the club uses it:</strong> your contact scans the QR
              or opens the link, checks the request, and confirms with a code
              sent to their phone. No account needed.
            </span>
          </div>
          <p className="text-[11.5px] text-[#94A3B8] pl-[21px]">
            You never enter a phone number. Confirming means only that the
            club recognizes this need and this agent as its channel.
          </p>
        </div>
      </div>
    </div>
  );

  // ── ≤768: full-screen bottom sheet (artboard 390) ──────────────────────────
  const sheet = (
    <div className="fixed inset-0 bg-[rgba(15,23,42,0.42)] flex items-end justify-center z-50">
      <div className="bg-white rounded-t-[18px] w-full h-[93%] shadow-[0_-12px_40px_rgba(15,23,42,0.25)] flex flex-col overflow-hidden">
        <div className="pt-2.5 flex justify-center">
          <span className="w-9 h-1 rounded-full bg-[#E2E8F0]" />
        </div>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F1F5F9]">
          <h3 className="text-[14.5px] font-bold text-[#0F172A]">Verify your request</h3>
          <span className="flex-1" />
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg border border-[#E2E8F0] inline-flex items-center justify-center text-[#64748B]"
            aria-label="Close"
          >
            <CloseGlyph size={12} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-[13px]">
          {contextPill && (
            <div className="text-xs text-[#64748B]">{contextPill}</div>
          )}
          {gateBox(true)}
          {/* QR + link — one card on mobile. */}
          <div className="flex flex-col items-center gap-[9px] border border-[#E2E8F0] rounded-xl p-4">
            <div className="flex justify-center" ref={qrWrapRef}>
              {armed && qrUrl ? (
                <QRCodeCanvas value={qrUrl} size={190} marginSize={3} />
              ) : (
                <div
                  className="w-[190px] h-[190px] rounded-[10px] bg-gray-100 border border-dashed border-[#E2E8F0]"
                  data-testid="qr-placeholder"
                />
              )}
            </div>
            <div className="flex gap-2 self-stretch">
              <button
                onClick={handleQrShareOrDownload}
                disabled={disabledWay}
                className={`flex-1 ${wayBtn("border border-[#E2E8F0] bg-white text-[#334155]", true)}`}
              >
                <IconDownload /> Save QR
              </button>
              <button
                onClick={handleShareLink}
                disabled={disabledWay}
                className={`flex-1 ${wayBtn("bg-[#2283FF] text-white", true)}`}
              >
                <IconShare /> Share link
              </button>
            </div>
            <button
              onClick={handleCopy}
              disabled={disabledWay}
              className="self-stretch h-9 border border-[#E2E8F0] rounded-[9px] bg-[#F8FAFC] flex items-center justify-between px-[11px] text-[11.5px] text-[#475569] font-mono disabled:text-[#94A3B8]"
            >
              <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                {armed ? linkUrl : "Link appears once armed"}
              </span>
              <span className="flex-none text-[#64748B]"><IconCopy size={12} /></span>
            </button>
            {onePagerLink}
          </div>
          {/* Send by email (optional). */}
          <div className="border border-[#E2E8F0] rounded-xl px-3.5 py-[13px] flex flex-col gap-[9px]">
            <div className="text-[12.5px] font-bold">
              Send by email{" "}
              <span className="font-medium text-[#94A3B8]">(optional)</span>
            </div>
            {emailField(true)}
            {sendEmailBtn(true)}
          </div>
          {banners}
          <p className="text-[11.5px] text-[#94A3B8]">
            Your contact scans the QR or opens the link, checks the request,
            and confirms with a code sent to their phone. No account needed.
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(isDesktop ? desktop : sheet, document.body);
};

export default VerifyNowModal;
